/**
 * CallNotesModal Component
 *
 * Draggable floating panel for viewing and editing notes after processing is complete.
 */

import { useState, useRef, useEffect } from 'react'
import { CheckCircle2, AlertCircle, X, Minimize2, Maximize2, GripHorizontal } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useCallRecordingContext } from '@/contexts/CallRecordingContext'
import { CallNotesEditor } from './CallNotesEditor'
import { cn } from '@/lib/utils'

interface CallNotesModalProps {
  isOpen: boolean
  onClose: () => void
}

export function CallNotesModal({ isOpen, onClose }: CallNotesModalProps) {
  const { ticketId, recordingId, status, error, reset } = useCallRecordingContext()

  const [position, setPosition] = useState({ x: 0, y: 0 })
  const [isDragging, setIsDragging] = useState(false)
  const [isMinimized, setIsMinimized] = useState(false)
  const dragRef = useRef<{ startX: number; startY: number; initialX: number; initialY: number } | null>(null)
  const panelRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (typeof window !== 'undefined' && isOpen) {
      setPosition({ x: Math.max(10, window.innerWidth - 520), y: Math.max(20, (window.innerHeight - 600) / 2) })
    }
  }, [isOpen])

  const handleClose = () => { reset(); onClose() }

  const handleMouseDown = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('button')) return
    setIsDragging(true)
    dragRef.current = { startX: e.clientX, startY: e.clientY, initialX: position.x, initialY: position.y }
  }

  useEffect(() => {
    if (!isDragging) return
    const handleMouseMove = (e: MouseEvent) => {
      if (!dragRef.current) return
      setPosition({
        x: Math.max(0, Math.min(window.innerWidth - 500, dragRef.current.initialX + (e.clientX - dragRef.current.startX))),
        y: Math.max(0, Math.min(window.innerHeight - 100, dragRef.current.initialY + (e.clientY - dragRef.current.startY))),
      })
    }
    const handleMouseUp = () => { setIsDragging(false); dragRef.current = null }
    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('mouseup', handleMouseUp)
    return () => { window.removeEventListener('mousemove', handleMouseMove); window.removeEventListener('mouseup', handleMouseUp) }
  }, [isDragging])

  const showPanel = isOpen && recordingId && (status === 'complete' || status === 'error')
  if (!showPanel) return null

  if (isMinimized) {
    return (
      <div ref={panelRef} style={{ left: position.x, top: position.y }} className="fixed z-50 cursor-move" onMouseDown={handleMouseDown}>
        <div className="flex items-center gap-2 px-4 py-2 bg-green-500 rounded-full shadow-lg">
          <CheckCircle2 className="h-4 w-4 text-white" />
          <span className="text-white text-sm font-medium">Recording Ready</span>
          <Button variant="ghost" size="icon" className="h-6 w-6 text-white hover:bg-white/20" onClick={(e) => { e.stopPropagation(); setIsMinimized(false) }}><Maximize2 className="h-4 w-4" /></Button>
          <Button variant="ghost" size="icon" className="h-6 w-6 text-white hover:bg-white/20" onClick={(e) => { e.stopPropagation(); handleClose() }}><X className="h-4 w-4" /></Button>
        </div>
      </div>
    )
  }

  return (
    <div ref={panelRef} style={{ left: position.x, top: position.y }} className={cn('fixed z-50 w-[480px] max-h-[80vh] bg-white rounded-lg shadow-2xl border border-gray-200 overflow-hidden flex flex-col', isDragging && 'cursor-grabbing')}>
      <div className={cn('flex items-center justify-between px-4 py-3 cursor-move border-b', status === 'error' ? 'bg-red-500' : 'bg-green-500')} onMouseDown={handleMouseDown}>
        <div className="flex items-center gap-2">
          <GripHorizontal className="h-4 w-4 text-white/60" />
          {status === 'error' ? <AlertCircle className="h-5 w-5 text-white" /> : <CheckCircle2 className="h-5 w-5 text-white" />}
          <div className="text-white">
            <span className="font-medium">{status === 'error' ? 'Error' : 'Recording Complete'}</span>
            {ticketId && <span className="text-white/80 text-sm ml-2">Ticket #{ticketId}</span>}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" className="h-7 w-7 text-white hover:bg-white/20" onClick={(e) => { e.stopPropagation(); setIsMinimized(true) }} title="Minimize"><Minimize2 className="h-4 w-4" /></Button>
          <Button variant="ghost" size="icon" className="h-7 w-7 text-white hover:bg-white/20" onClick={(e) => { e.stopPropagation(); handleClose() }} title="Close"><X className="h-4 w-4" /></Button>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-4">
        {status === 'complete' && recordingId && (
          <CallNotesEditor recordingId={recordingId} ticketId={ticketId ?? undefined} onAddedToTicket={() => { reset(); onClose() }} />
        )}
        {status === 'error' && (
          <div className="space-y-6 py-4">
            <div className="flex flex-col items-center gap-4">
              <AlertCircle className="h-12 w-12 text-red-500" />
              <div className="text-center">
                <p className="font-medium text-red-600">Something went wrong</p>
                <p className="text-sm text-gray-500 mt-1">{error || 'An unexpected error occurred'}</p>
              </div>
            </div>
            <div className="flex justify-center gap-2"><Button variant="outline" onClick={handleClose}>Close</Button></div>
          </div>
        )}
      </div>
    </div>
  )
}
